import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { getVoterToken } from '@/lib/voterToken';
import type { Tables } from '@/integrations/supabase/types';

type Model = Tables<'models'>;
type PriceDK = Tables<'prices_dk'>;

export interface CompetitorCandidate {
  modelId: string;
  brandSlug: string;
  modelSlug: string;
  brandName: string;
  modelName: string;
  imageUrl: string | null;
  score: number;
}

export interface CompetitorVoteAggregate {
  candidate: CompetitorCandidate;
  count: number;
  voted: boolean;
}

export interface VariantCompetitorVotesData {
  candidates: CompetitorVoteAggregate[];
  /** All active models (for search/pick) */
  allModels: CompetitorCandidate[];
  totalVotes: number;
  myVoteModelId: string | null;
  /** Model with most votes */
  topModelId: string | null;
}

interface Args {
  variantId: string;
  currentModel: Model;
  currentPrice: number | null;
}

function scoreModel(
  current: { model: Model; price: number | null },
  cand: { model: Model; price: number | null },
): number {
  let s = 0;
  if (current.model.body_type && current.model.body_type === cand.model.body_type) s += 30;
  if (current.price && cand.price) {
    const d = Math.abs(current.price - cand.price) / current.price;
    if (d <= 0.2) s += 30 * (1 - d / 0.2);
  }
  return s;
}

export function useVariantCompetitorVotes({ variantId, currentModel, currentPrice }: Args) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const userId = user?.id ?? null;
  const token = getVoterToken();
  const currentModelId = currentModel.id;

  const candidatesQuery = useQuery({
    queryKey: ['competitor-candidates-model', currentModelId],
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<CompetitorCandidate[]> => {
      const { data, error } = await supabase
        .from('models')
        .select(`
          id, name, slug, body_type, image_url,
          brands!inner ( id, name, slug ),
          variants!inner ( id, image_url, is_active, prices_dk ( price_dkk ) )
        `)
        .eq('is_active', true)
        .neq('id', currentModelId);
      if (error) throw error;
      const list = (data || []) as any[];

      const candidates: CompetitorCandidate[] = [];
      for (const m of list) {
        const brand = m.brands;
        const activeVariants = (m.variants || []).filter((v: any) => v.is_active);
        if (!activeVariants.length) continue;
        const allPrices = activeVariants.flatMap((v: any) =>
          ((v.prices_dk || []) as PriceDK[]).map((p) => p.price_dkk),
        );
        const price = allPrices.length ? Math.min(...allPrices) : null;
        const fallbackImage = activeVariants.find((v: any) => v.image_url)?.image_url || null;
        const s = scoreModel(
          { model: currentModel, price: currentPrice },
          { model: m as Model, price },
        );
        candidates.push({
          modelId: m.id,
          brandSlug: brand.slug,
          modelSlug: m.slug,
          brandName: brand.name,
          modelName: m.name,
          imageUrl: m.image_url || fallbackImage,
          score: s,
        });
      }

      return candidates.sort((a, b) => b.score - a.score);
    },
  });

  const votesQuery = useQuery({
    queryKey: ['variant-competitor-votes-model', variantId, userId, token],
    enabled: !!variantId,
    staleTime: 30_000,
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc('get_variant_competitor_vote_summary', {
        _variant_id: variantId,
        _voter_token: userId ? null : token,
      });
      if (error) throw error;
      return ((data || []) as unknown) as {
        competitor_model_id: string;
        count: number;
        total_votes: number;
        my_vote_model_id: string | null;
      }[];
    },
  });

  const setVote = useMutation({
    mutationFn: async (competitorModelId: string) => {
      const { error } = await (supabase as any).rpc('set_competitor_vote', {
        _variant_id: variantId,
        _competitor_model_id: competitorModelId,
        _voter_token: userId ? null : token,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['variant-competitor-votes-model', variantId] });
    },
  });

  const clearVote = useMutation({
    mutationFn: async () => {
      const { error } = await (supabase as any).rpc('clear_competitor_vote', {
        _variant_id: variantId,
        _voter_token: userId ? null : token,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['variant-competitor-votes-model', variantId] });
    },
  });

  const allModels = candidatesQuery.data || [];
  const suggested = allModels.filter((c) => c.score >= 15).slice(0, 5);
  const rows = votesQuery.data || [];

  const countMap = new Map<string, number>();
  let totalVotes = 0;
  let myVote: string | null = null;
  for (const r of rows) {
    countMap.set(r.competitor_model_id, r.count);
    totalVotes = r.total_votes ?? totalVotes;
    if (r.my_vote_model_id) myVote = r.my_vote_model_id;
  }

  const byId = new Map(allModels.map((c) => [c.modelId, c]));
  const shown = new Map<string, CompetitorCandidate>();
  // 1) Models with votes, most voted first
  for (const [mid] of [...countMap.entries()].sort((a, b) => b[1] - a[1])) {
    const c = byId.get(mid);
    if (c) shown.set(mid, c);
  }
  // 2) Always include my own vote
  if (myVote && byId.has(myVote)) shown.set(myVote, byId.get(myVote)!);
  // 3) Fill up to 5 with suggested competitors
  for (const c of suggested) {
    if (shown.size >= 5) break;
    if (!shown.has(c.modelId)) shown.set(c.modelId, c);
  }

  const aggregates: CompetitorVoteAggregate[] = [...shown.values()]
    .map((c) => ({
      candidate: c,
      count: countMap.get(c.modelId) || 0,
      voted: c.modelId === myVote,
    }))
    .sort((a, b) => b.count - a.count || a.candidate.brandName.localeCompare(b.candidate.brandName));

  let topModelId: string | null = null;
  let topCount = 0;
  for (const [mid, c] of countMap.entries()) {
    if (c > topCount) {
      topCount = c;
      topModelId = mid;
    }
  }

  const data: VariantCompetitorVotesData = {
    candidates: aggregates,
    allModels,
    totalVotes,
    myVoteModelId: myVote,
    topModelId,
  };

  return {
    data,
    isLoading: candidatesQuery.isLoading || votesQuery.isLoading,
    setVote: setVote.mutate,
    clearVote: clearVote.mutate,
    isPending: setVote.isPending || clearVote.isPending,
  };
}
